import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";
import SearchStatusBar from "@/components/common/SearchStatusBar";
import BudgetEditDialog from "@/features/ads/BudgetEditDialog";
import { type AdSet, type Campaign, statusBadge } from "@/features/ads/types";
import { Checkbox } from "@/components/ui/checkbox";
import BatchActionBar from "@/features/batch/BatchActionBar";
import BatchCopyDialog from "@/features/batch/BatchCopyDialog";
import { useSelection } from "@/features/batch/useSelection";

const STATUS_OPTIONS = [
  { value: "ALL",      label: "全部状态" },
  { value: "ACTIVE",   label: "ACTIVE" },
  { value: "PAUSED",   label: "PAUSED" },
  { value: "DELETED",  label: "DELETED" },
  { value: "ARCHIVED", label: "ARCHIVED" },
];

export default function CampaignDetailPage() {
  const { accountId = "", campaignId = "" } = useParams();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [budgetEdit, setBudgetEdit] = useState<AdSet | null>(null);
  const [copyTarget, setCopyTarget] = useState<AdSet | null>(null);
  const sel = useSelection();
  const invalidateAdSets = () => qc.invalidateQueries({ queryKey: ["adsets", accountId, campaignId] });

  // Share the cache with CampaignsSection (same key + same range).
  const campaigns = useQuery({
    queryKey: ["campaigns", accountId, "last_30d"],
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/v1/ad-accounts/{accountId}/campaigns",
        { params: { path: { accountId }, query: { range: "last_30d" } as never } }
      );
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Campaign[];
    },
  });
  const campaign = campaigns.data?.find((c) => c.id === campaignId);

  const adsets = useQuery({
    queryKey: ["adsets", accountId, campaignId],
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/v1/ad-accounts/{accountId}/campaigns/{campaignId}/adsets",
        { params: { path: { accountId, campaignId } } }
      );
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as AdSet[];
    },
    staleTime: 15_000,
  });

  const filtered = useMemo(() => {
    const rows = adsets.data ?? [];
    const needle = q.trim().toLowerCase();
    return rows.filter((s) => {
      if (status !== "ALL" && s.status !== status) return false;
      if (needle && !((s.name ?? "") + " " + s.id).toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [adsets.data, q, status]);

  const copy = useMutation({
    mutationFn: async (args: { id: string; newStatus: string; startTime: string | null; endTime: string | null }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/ad-accounts/{accountId}/adsets/{adsetId}/copy",
        {
          params: { path: { accountId, adsetId: args.id } },
          body: {
            newStatus: args.newStatus,
            startTime: args.startTime,
            endTime: args.endTime,
          } as never,
        }
      );
      if (error || !response.ok) throw error || new Error("复制失败");
      return data as unknown as { newId: string };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["adsets", accountId, campaignId] });
      toast.success(`复制成功：${r.newId}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE(
        "/api/v1/ad-accounts/{accountId}/adsets/{adsetId}",
        { params: { path: { accountId, adsetId: id } } }
      );
      if (error || !response.ok) throw error || new Error("删除失败");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adsets", accountId, campaignId] });
      toast.success("已删除");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/ad-accounts/${accountId}`} className="text-xs text-muted-foreground hover:underline">
          ← 返回广告账户
        </Link>
        <h1 className="text-2xl font-semibold mt-2">
          {campaign?.name ?? "Campaign"}
          {campaign?.status && (
            <span className={`ml-3 text-xs px-2 py-0.5 rounded align-middle ${statusBadge(campaign.status)}`}>
              {campaign.status}
            </span>
          )}
        </h1>
        <div className="font-mono text-xs text-muted-foreground mt-1">{campaignId}</div>
      </div>

      {budgetEdit && (
        <BudgetEditDialog
          level="adset"
          accountId={accountId}
          targetId={budgetEdit.id}
          campaignIdForAdsetInvalidate={campaignId}
          currentDailyCents={budgetEdit.dailyBudgetCents}
          currentLifetimeCents={budgetEdit.lifetimeBudgetCents}
          onClose={() => setBudgetEdit(null)}
        />
      )}

      {copyTarget && (
        <BatchCopyDialog
          open={!!copyTarget}
          onOpenChange={(o) => { if (!o) setCopyTarget(null); }}
          count={1}
          resourceLabel="AdSet"
          onSubmit={({ newStatus, startTime, endTime }) => {
            copy.mutate({ id: copyTarget.id, newStatus, startTime, endTime });
            setCopyTarget(null);
          }}
        />
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-medium text-lg">AdSets</h2>
          <SearchStatusBar
            q={q}
            onQChange={setQ}
            status={status}
            onStatusChange={setStatus}
            statusOptions={STATUS_OPTIONS}
            placeholder="搜索 AdSet 名称或 ID"
          />
        </div>

        {sel.count > 0 && (
          <BatchActionBar
            accountId={accountId}
            level="adsets"
            resourceLabel="AdSet"
            selectedIds={sel.ids}
            onClear={sel.clear}
            onSuccess={invalidateAdSets}
          />
        )}

        <div className="rounded-lg border border-border bg-background overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium w-8">
                  <Checkbox
                    aria-label="全选"
                    checked={
                      filtered.length > 0 && filtered.every((s) => sel.has(s.id))
                        ? true
                        : filtered.some((s) => sel.has(s.id))
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(v) => {
                      if (v === true) sel.setAll(filtered.map((s) => s.id));
                      else sel.clear();
                    }}
                  />
                </th>
                <th className="text-left px-4 py-2 font-medium">名称</th>
                <th className="text-left px-4 py-2 font-medium">优化目标</th>
                <th className="text-left px-4 py-2 font-medium">状态</th>
                <th className="text-right px-4 py-2 font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {adsets.isLoading && (
                <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">加载中…</td></tr>
              )}
              {!adsets.isLoading && adsets.data?.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">该 Campaign 暂无 AdSet</td></tr>
              )}
              {!adsets.isLoading && (adsets.data?.length ?? 0) > 0 && filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">没有符合筛选条件的 AdSet</td></tr>
              )}
              {filtered.map((s) => (
                <tr key={s.id} className="border-t border-border">
                  <td className="px-4 py-2">
                    <Checkbox
                      aria-label={`选择 ${s.name ?? s.id}`}
                      checked={sel.has(s.id)}
                      onCheckedChange={() => sel.toggle(s.id)}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <Link
                      to={`/ad-accounts/${accountId}/adsets/${s.id}`}
                      className="text-primary hover:underline"
                    >
                      {s.name ?? "(无名称)"}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-xs">{s.optimizationGoal ?? "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${statusBadge(s.status)}`}>
                      {s.status ?? "—"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right space-x-3 text-xs">
                    <button
                      onClick={() => setBudgetEdit(s)}
                      className="text-primary hover:underline"
                    >
                      改预算
                    </button>
                    <button
                      onClick={() => setCopyTarget(s)}
                      disabled={copy.isPending}
                      className="text-primary hover:underline disabled:opacity-50"
                    >
                      复制
                    </button>
                    <button
                      onClick={() => { if (confirm(`确定删除 AdSet "${s.name}"?`)) remove.mutate(s.id); }}
                      disabled={remove.isPending}
                      className="text-destructive hover:underline disabled:opacity-50"
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
